import ContactForm from "../../components/forms/contact";
import Newsletter from "../../components/newsletter";
import ProgramAlumni from "../../components/program/programAlumni";
import ProgramExperiences from "../../components/program/programExperiences";
import ProgramInfo from "../../components/program/programInfo";
import ProgramOpenDay from "../../components/program/programOpenDay";
import ProgramSummary from "../../components/program/programSummary";
import RelatedPrograms from "../../components/program/relatedPrograms";
import TravelFlexibility from "../../components/program/travelFlexibility";
import EnquireCard from "../../components/shared/enquireCard";
import MobileGallery from "../../components/shared/gallery/mobileGallery";
import Hero from "../../components/shared/hero";
import Modal from "../../components/shared/modal";
import ModalInPortal from "../../components/shared/modals/modalInPortal";
import Testimonials from "../../components/shared/testimonials";
import TestimonialsMobile from "../../components/shared/testimonialsMobile";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useProgramTestimonials } from "../../hooks/useProgramTestimonials";
import { flattenMenu } from "../../utils/flattenMenu";
import { formatHeroBackgroundOptions } from "../../utils/formatContent";
import { DesktopTabletOnly, MobileOnly } from "../base/wrapper/device";
import StickyFooter from "../shared/sticky/StickyWithEmbeddedModal";
import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "images/icons/close-outline.inline.svg";
import TestiLightDesktopBg from "images/light-testimonial-bg-desktop.png";
import TestiLightMobileBg from "images/light-testimonial-bg-mobile.svg";
import TestiDarkDesktopBg from "images/testimonial-bg-image.svg";
import TestiDarkMobileBg from "images/testimonial-bg-mobile.png";

const ProgramPageComponent = ({ data, location, theme }) => {
  const containerRef = useRef(null);
  const stickyRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  const stickyBarRef = useRef(null);

  const [openAlumniModal, setOpenAlumniModal] = useState(false);
  const [openParentModal, setOpenParentModal] = useState(false);
  const [openItineraryOpenModal, setItineraryOpenModal] = useState(false);
  const [getActiveTabSlidebySlug, setActiveTabSlidebySlug] = useState(null);
  const [getProgramTestimonials, setProgramTestimonials] = useState([]);

  const program = data?.program || {};
  const programDetails = program?.acfProgramsMeta;
  const programGeographicals = program?.geographical ?? [];
  const sharedDetails = data?.genericDetails ?? [];
  const programName = program?.title ?? "";
  const programId = program?.databaseId ?? "";
  const programCode = programDetails?.programCode ?? programId;
  const programSlug = program?.slug ?? program?.uri;

  const programIdentifier = programCode ?? programId;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  const applyNow = programId
    ? `/application-form/?${programSuffix}`
    : "/application-form/";
  const enquireNow = programId
    ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}`
    : "/enquiry-form/";
  const programTitle = programDetails?.programTitle2 ?? "";
  const programPopular = programDetails?.programPopular ?? false;
  const programCapacity = programDetails?.programCapacity ?? "";
  const programDuration = programDetails?.durationDisplay ?? "";
  const programFocus = programDetails?.programFocuses ?? [];
  const programFeaturedVideo =
    programDetails?.projectVideo?.length > 50
      ? programDetails?.projectVideo
      : "";
  const programFeaturedVideoTestimonials =
    programDetails?.programFeaturedVideoTestimonials ?? "";
  const programAlumniGallery =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig
      ?.programAlumniGallery ?? [];
  const programHeading =
    programDetails?.programHeaderOverlayTitle ??
    "Preserve a vibrant <br/>island ecosystem";

  const heroOpts = formatHeroBackgroundOptions(programDetails);

  const programFeaturedImage = heroOpts?.image?.desktop;
  const programFeaturedMobileImage = heroOpts?.image?.mobile;

  const programGallery = programDetails?.gallery ?? [];

  //filter out svg files from the images
  //mobile gallery should include featured image
  const mobileFilterGalleryCollection = programGallery?.filter(
    (item) =>
      !(item.localFile?.extension === "svg") &&
      !(item.localFile?.extension === "")
  );

  //filter out featured image
  const filterGalleryCollection = mobileFilterGalleryCollection?.filter(
    (item) => item.id !== programFeaturedImage.id
  );

  const filteredGallery = programFeaturedVideo
    ? filterGalleryCollection
    : filterGalleryCollection.slice(1);
  const mobileVideoGallery = [];
  if (
    programFeaturedVideoTestimonials &&
    programFeaturedVideoTestimonials.length > 0
  ) {
    programFeaturedVideoTestimonials.forEach((videos) => {
      mobileVideoGallery.push({
        genVideoUrl: videos?.programFeaturedVideo
      });
    });
  }

  const programGalleryFeaturedImage =
    filterGalleryCollection[0].sourceUrl ??
    program?.featuredImage?.node?.sourceUrl ??
    "";

  const programTestimonials =
    useProgramTestimonials({
      programId: programCode,
      currentBrand: theme?.currentBrand ?? "planet"
    }) ?? [];

  const programTypes = programDetails.programTypes?.flatMap(
    (item) => item.slug
  );

  const programTypesFull = programDetails.programTypes;

  const flatGeoArr = programGeographicals?.nodes.map((geo) => geo);
  const nestedGeo = flattenMenu(flatGeoArr, {
    idKey: "id",
    parentKey: "parentId",
    childrenKey: "children"
  });
  const programHub = nestedGeo
    ?.flatMap((item) => item?.children?.flatMap((child) => child?.children))
    .shift();
  const geographicalNavLinks = [];
  if (nestedGeo && nestedGeo.length) {
    nestedGeo.forEach((geographical, index) => {
      geographicalNavLinks.push({
        imgPath: "location-icon.svg",
        toPage: geographical?.geographicalTaxonomy?.pageLink?.uri ?? "#",
        text: `GVI ${geographical.name}`
      });

      geographical.children.forEach((childG) => {
        geographicalNavLinks.push({
          imgPath: null,
          toPage: childG?.geographicalTaxonomy?.pageLink?.uri ?? "#",
          text: childG.name
        });

        childG.children.forEach((gChild) => {
          geographicalNavLinks.push({
            imgPath: null,
            toPage: gChild?.geographicalTaxonomy?.pageLink?.uri ?? "#",
            text: gChild.name
          });
        });
      });
    });
  }

  const relatedGeoSlugs = flatGeoArr?.flatMap((geo) => geo.slug);

  // const programLocationPage = programHub?.locationPages?.nodes?.shift()?.uri;
  const programUri = program.databaseId;
  const heroNavLinks = geographicalNavLinks;

  const experiences =
    programHub?.acfCountryExploration?.gviExperiences?.filter((item) => item) ??
    [];

  const {
    bookWithConfidenceText,
    bookWithConfidenceTitle,
    programImpactTitle,
    programImpactList,
    ueStickySchedule
  } = sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails(programSlug || null) ?? {};

  const enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: stickyImage,
    cardDesc:
      `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: null, //stickyBookDesc ?? "Book by"
    shareButton: false,
    language: theme?.localeLanguage
  };

  const testimonialHeading = "Want to live a wilder life? Talk to us!";
  const testimonialSubheading =
    "During office hours, we'll get back to you within a few minutes.";
  const testimonialCtaLabel = "Get more information";
  const travelDetails = {
    title: bookWithConfidenceTitle ?? "Book with confidence",
    description:
      bookWithConfidenceText ??
      `Modify your booking, even up to 48 hours before departure. <a class='underline inline-block' href='/terms-and-conditions' rel='nofollow' target='_gviblank'>T&Cs apply.</a>`
  };

  const currentDate = new Date();

  const teenStickyDetailsSchedule =
    ueStickySchedule
      ?.filter((schedule) => {
        return (
          currentDate.getTime() >=
            Date.parse(
              schedule?.scheduledStartDate?.split("/")?.reverse()?.join("-")
            ) &&
          currentDate.getTime() <=
            Date.parse(
              schedule?.scheduledEndDate?.split("/")?.reverse()?.join("-")
            )
        );
      })
      .shift() ?? {};

  const teenStickyDetails = {
    title: teenStickyDetailsSchedule?.stickyTitle ?? "",
    description: teenStickyDetailsSchedule?.stickyDescription ?? ""
  };

  const impact = programImpactList?.map((item) => {
    return item?.impact;
  });

  const programImpact = {
    title: programImpactTitle ?? "Program impact",
    description: impact ?? []
  };

  const liveUpdates = programHub?.geographicalTaxonomy ?? "";

  const countryExploration =
    sharedDetails.countryExploration &&
    sharedDetails.countryExploration.acfCountryExploration
      ? sharedDetails.countryExploration.acfCountryExploration
      : "";
  const expHeading =
    countryExploration?.gviExperiencesSectionHeading ??
    "Included in your program";
  const expSubHeading =
    countryExploration?.gviExperiencesSectionSubheading ??
    "A variety of the following exclusive, locally-curated adventure and wellness activities are available on a monthly basis.";
  const expTabSubtitle =
    countryExploration?.gviExperiencesTabSubtitle ??
    "Offered once a month, expand your adventure with GVI Experiences. These are just some of the activities offered on your program!";
  const expTabTitle =
    countryExploration?.gviExperiencesLabel ??
    "GVI experiences included in your program, <span> at no extra cost</span>";

  const genericConfig =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const parentFormType = "parent_request";
  const parentFormHeading =
    genericConfig?.parentConsultationHeading ?? "Free parent consultation";
  const parentFormSubheading =
    genericConfig?.parentConsultationSubheading ?? "";

  const itineraryDownloadFormHeading = "Enter your details <br/> to download";
  const itineraryDownloadFormType = programTypes?.includes("under-18")
    ? "itinerary_under18_form"
    : "itinerary_form";
  const itineraryDownloadFormHiddenFields = [
    { fieldName: "program_id", fieldValue: programCode },
    { fieldName: "under18", fieldValue: programTypes?.includes("under-18") }
  ];

  const handleCloseModal = () => {
    setItineraryOpenModal(false);
  };

  const hiddenFields = [{ fieldName: "program_id", fieldValue: programCode }];

  const locationPages = programHub?.slug ?? "";

  const itineraryDetails = sharedDetails?.itinerary?.itinerary || [];

  //filter the collection to the program location & product type
  const itineraryCollection = itineraryDetails?.itineraryCollection?.filter(
    (item) => {
      return (
        item?.gitcProductType?.some((type) =>
          programTypes?.includes(type.slug)
        ) &&
        item?.gitcGeographical?.some((geo) => locationPages?.includes(geo.slug))
      );
    }
  );

  const programItineraryFile =
    programDetails?.programItineraryFile?.mediaItemUrl ||
    (itineraryCollection?.length > 0
      ? itineraryCollection[0]?.gitcItineraryFile?.mediaItemUrl
      : null) ||
    null;

  const displayProgramType =
    programTypesFull
      ?.flatMap((item) => item.title)
      ?.shift()
      ?.split(" ")
      ?.join(" <br/>") || "";

  useEffect(() => {
    programTestimonials
      .then((res) => setProgramTestimonials(res))
      .catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyBarRef.current) {
        const top = containerRef.current.getBoundingClientRect().top;
        if (top >= window.innerHeight) {
          stickyBarRef.current.style.bottom = "-168px";
        } else {
          stickyBarRef.current.style.bottom = "0";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  return (
    <>
      <MobileOnly>
        <MobileGallery
          containerClass={
            "ingo-mobile-gallery mobile-gallery-section relative bg-transparent h-56 w-full sm:hidden"
          }
          gallery={filterGalleryCollection}
          featuredSingleVideo={programFeaturedVideo}
          programGalleryFeaturedImage={""}
          videoGallery={mobileVideoGallery}
        />
      </MobileOnly>
      <DesktopTabletOnly>
        <Hero
          navLinks={heroNavLinks}
          heroHeadLine={programHeading}
          heroImg={programFeaturedImage}
          heroImageMobile={programFeaturedMobileImage}
          subtextTitle={displayProgramType}
        />
      </DesktopTabletOnly>

      <ProgramSummary
        programName={programName}
        programTitle={programTitle}
        programDuration={programDuration}
        programFocus={programFocus}
        programFeaturedVideo={programFeaturedVideo}
        programUri={programUri}
        programId={programId}
        programCode={programCode}
        programPopular={programPopular}
        programCapacity={programCapacity}
        gallery={filteredGallery}
        programGalleryFeaturedImage={programGalleryFeaturedImage}
        programFeaturedVideoTestimonials={programFeaturedVideoTestimonials}
        programType={programTypes}
        programTypes={programTypesFull}
        setOpenModal={setOpenParentModal}
        openModal={openParentModal}
        formType={parentFormType}
        formSubheading={parentFormSubheading}
        formHeading={parentFormHeading}
        travelDetails={travelDetails}
        setActiveTabSlidebySlug={setActiveTabSlidebySlug}
        liveUpdates={liveUpdates}
        galleryContainerClass={
          "hidden md:grid grid-flow-col grid-cols-4 grid-rows-2 gap-0 program-gallery sm:h-96 sm:gap-4"
        }
        galleryViewMoreContainerClass={
          "hidden md:flex flex-row justify-end py-4 sm:absolute sm:bottom-3 sm:right-3"
        }
      />
      <div className="relative sticky-wrapper sm:overflow-x-clip">
        <ProgramInfo
          programDetails={programDetails}
          excerpt={program.content}
          programLocation={geographicalNavLinks}
          sharedDetails={sharedDetails}
          testimonials={getProgramTestimonials}
          program={program}
          experiences={experiences}
          expTabTitle={expTabTitle}
          programHub={programHub}
          programPopular={programPopular}
          programCapacity={programCapacity}
          programImpact={programImpact}
          setAlumniOpenModal={setOpenAlumniModal}
          location={location}
          liveUpdates={liveUpdates}
          setItineraryOpenModal={setItineraryOpenModal}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          enquireCardExtraDetails={enquireCardDetails}
        />
        <div className="hidden w-full md:absolute md:h-full md:sticky-position-programs md:w-auto md:flex">
          <div className="relative w-[355px]" ref={containerRef}>
            <div ref={stickyRef} className={`card-sticky-container absolute`}>
              <EnquireCard
                applyNow={applyNow}
                enquireNow={enquireNow}
                enquireCardDetails={enquireCardDetails}
              />
              <TravelFlexibility
                travelDetails={travelDetails}
                isGVI={true}
                hasBullets={false}
                outerContainerClass="border-0.5 sm:h-full my-6 bg-yellow-tertiary dark:bg-generic-bg"
                innerContainerClass="p-6"
                headingClass="sm:text-xl sm:font-semibold font-body text-black-lighter dark:text-white pb-3"
                listItemClass="text-xs"
              />
            </div>
          </div>
        </div>
      </div>
      {!programTypes?.includes("under-18") && (
        <div className="bg-yellow-lighter dark:bg-gray-dark">
          <ProgramExperiences
            experiences={experiences}
            expHeading={expHeading}
            expSubHeading={expSubHeading}
            expTabSubtitle={expTabSubtitle}
            expBgColor="h-16 sm:h-14 bg-white dark:bg-generic-bg"
          />
        </div>
      )}

      {(teenStickyDetails?.title || teenStickyDetails?.description) &&
        programTypes?.includes("under-18") && (
          <StickyFooter
            teenStickyDetails={teenStickyDetails}
            applyNow={applyNow}
            programCode={programCode}
            setOpenModal={setOpenParentModal}
            ref={stickyBarRef}
            containerRefClass={""}
          />
        )}

      <ProgramAlumni
        alumniGallery={programAlumniGallery}
        gallery={filteredGallery}
        setOpenModal={setOpenAlumniModal}
        openModal={openAlumniModal}
        programCode={programCode}
      />
      <ProgramOpenDay />
      <MobileOnly>
        <TestimonialsMobile testimonials={getProgramTestimonials} />
      </MobileOnly>
      <DesktopTabletOnly>
        <Testimonials
          testimonials={getProgramTestimonials}
          heading={testimonialHeading}
          subHeading={testimonialSubheading}
          ctaLabel={testimonialCtaLabel}
          type="program"
          form_type="enquiry_form"
          bgSrc={{
            light: {
              desktop: TestiLightDesktopBg,
              mobile: TestiLightMobileBg
            },
            dark: { desktop: TestiDarkDesktopBg, mobile: TestiDarkMobileBg }
          }}
          hiddenFields={hiddenFields}
          formTooltipIconStyles={"fill-white"}
          calendlyLayout={"popup"}
          calendlyDesc={
            "Alternatively, schedule a call time above via Calendly."
          }
          formStatusExtraContainerClass={""}
          formStatusIconColorClass={"text-white fill-white"}
          formStatusHeadingTextProcessing={"Just a moment..."}
          formStatusBriefTextProcessing={
            "We're making sure we have everything we need before connecting you with one of our team members."
          }
          formStatusHeadingTextSuccess={"Thank you!"}
          formStatusBriefTextSuccess={
            "One of our expert consultants will be in touch within 12 hours. If you can't wait to speak to us, you can call us on INTL_NUMBER"
          }
          formAdditionalFieldContainerClass="xxs:gap-2"
          formContactLabelsClass="text-white sm:text-black-lighter dark:text-white"
        />
      </DesktopTabletOnly>

      <RelatedPrograms
        relatedGeoSlugs={relatedGeoSlugs}
        excludeSlug={programSlug}
      />

      <Newsletter />

      {openItineraryOpenModal && (
        <>
          <ModalInPortal
            openModal={openItineraryOpenModal}
            modal={
              <Modal
                closeModal={handleCloseModal}
                modalCloseIcon={
                  <CloseIcon className="h-5 fill-white dark:fill-white" />
                }
                modalContentStyles="w-full mx-auto bg-yellow-standard md:blur-none dark:gvi-blur-newsletter-bg dark:gvi-blur-bg gvi-border-light border-0.5 border-gray-testimonial rounded-xxs py-3 z-10"
                modalContainerStyles="w-full sm:w-3/5 h-full sm:h-auto"
                modalOuterContainerStyles={
                  "fixed top-0 left-0 right-0 overflow-scroll z-20 flex items-center justify-center w-full h-full sm:modalBackground bg-white dark:bg-gray-darkest sm:gvi-modal-light-bg dark:sm:bg-gray-darker/80"
                }
                modalCloseBtnStyles="hidden"
                modalContent={
                  <div className="px-4 sm:px-16 pt-4 pb-8">
                    <ContactForm
                      formType={itineraryDownloadFormType}
                      formHeading={itineraryDownloadFormHeading}
                      formStyles="rounded-md w-full mx-auto"
                      ctaLabel="Download"
                      hiddenFields={itineraryDownloadFormHiddenFields}
                      programItineraryFile={programItineraryFile}
                      downloadTitle={"Itinerary"}
                      formTooltipIconStyles={"fill-white"}
                      buttonClass={"w-full sm:w-1/2"}
                      iconColor={"fill-white stroke-white"}
                      contactLabelsClass="text-white"
                      calendlyActive={true}
                    />
                  </div>
                }
              />
            }
          ></ModalInPortal>
        </>
      )}
    </>
  );
};

export default ProgramPageComponent;
